import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  FormControl, InputLabel, Select, MenuItem, Grid, Alert,
  Checkbox, FormControlLabel, FormGroup, Chip, OutlinedInput
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import { getCentre, createCentre, updateCentre } from '../services/api';

const SPECIALITES = ['AL', 'ATTR-h', 'ATTR-wt', 'AA', 'cardiaque', 'neurologique', 'renale', 'digestive', 'genetique'];

function CentreEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [centre, setCentre] = useState({
    name: '', type: 'hopital', description: '',
    address: { street: '', city: '', postalCode: '', region: '' },
    coordinates: { lat: '', lng: '' },
    contact: { phone: '', email: '', website: '' },
    specialites: [], howToAccess: '', isVerified: false
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isNew) loadCentre();
  }, [id]);

  const loadCentre = async () => {
    try {
      const { data } = await getCentre(id);
      setCentre({
        ...data,
        address: data.address || { street: '', city: '', postalCode: '', region: '' },
        coordinates: data.coordinates || { lat: '', lng: '' },
        contact: data.contact || { phone: '', email: '', website: '' },
        specialites: data.specialites || []
      });
    } catch (error) {
      setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setCentre({ ...centre, [parent]: { ...centre[parent], [child]: value } });
    } else {
      setCentre({ ...centre, [field]: value });
    }
  };

  const handleSave = async () => {
    setError(''); setSuccess(''); setSaving(true);
    try {
      if (isNew) {
        await createCentre(centre);
        setSuccess('Centre cree');
        navigate('/centres');
      } else {
        await updateCentre(id, centre);
        setSuccess('Centre mis a jour');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Typography>Chargement...</Typography>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/centres')}>Retour</Button>
          <Typography variant="h4" fontWeight="bold">{isNew ? 'Nouveau centre' : 'Modifier'}</Typography>
        </Box>
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>Enregistrer</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Informations generales</Typography>
              <TextField fullWidth label="Nom" value={centre.name} onChange={(e) => handleChange('name', e.target.value)} margin="normal" required />
              <FormControl fullWidth margin="normal">
                <InputLabel>Type</InputLabel>
                <Select value={centre.type} label="Type" onChange={(e) => handleChange('type', e.target.value)}>
                  <MenuItem value="reference">Centre de reference</MenuItem>
                  <MenuItem value="competence">Centre de competence</MenuItem>
                  <MenuItem value="expert">Centre expert</MenuItem>
                  <MenuItem value="hopital">Hopital</MenuItem>
                </Select>
              </FormControl>
              <TextField fullWidth label="Description" value={centre.description} onChange={(e) => handleChange('description', e.target.value)} margin="normal" multiline rows={3} />
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Adresse</Typography>
              <TextField fullWidth label="Rue" value={centre.address.street} onChange={(e) => handleChange('address.street', e.target.value)} margin="normal" />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField fullWidth label="Code postal" value={centre.address.postalCode} onChange={(e) => handleChange('address.postalCode', e.target.value)} margin="normal" />
                </Grid>
                <Grid item xs={4}>
                  <TextField fullWidth label="Ville" value={centre.address.city} onChange={(e) => handleChange('address.city', e.target.value)} margin="normal" required />
                </Grid>
                <Grid item xs={4}>
                  <TextField fullWidth label="Region" value={centre.address.region} onChange={(e) => handleChange('address.region', e.target.value)} margin="normal" />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField fullWidth label="Latitude" type="number" value={centre.coordinates.lat} onChange={(e) => handleChange('coordinates.lat', e.target.value)} margin="normal" />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Longitude" type="number" value={centre.coordinates.lng} onChange={(e) => handleChange('coordinates.lng', e.target.value)} margin="normal" />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Contact</Typography>
              <TextField fullWidth label="Telephone" value={centre.contact.phone} onChange={(e) => handleChange('contact.phone', e.target.value)} margin="normal" />
              <TextField fullWidth label="Email" value={centre.contact.email} onChange={(e) => handleChange('contact.email', e.target.value)} margin="normal" />
              <TextField fullWidth label="Site web" value={centre.contact.website} onChange={(e) => handleChange('contact.website', e.target.value)} margin="normal" />
              <TextField fullWidth label="Comment prendre RDV" value={centre.howToAccess} onChange={(e) => handleChange('howToAccess', e.target.value)} margin="normal" multiline rows={3} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Specialites</Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel>Specialites</InputLabel>
                <Select
                  multiple
                  value={centre.specialites}
                  onChange={(e) => handleChange('specialites', e.target.value)}
                  input={<OutlinedInput label="Specialites" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (<Chip key={value} label={value} size="small" />))}
                    </Box>
                  )}
                >
                  {SPECIALITES.map((spec) => (<MenuItem key={spec} value={spec}>{spec}</MenuItem>))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Statut</Typography>
              <FormControlLabel
                control={<Checkbox checked={centre.isVerified} onChange={(e) => handleChange('isVerified', e.target.checked)} />}
                label="Centre verifie"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CentreEditor;
